import dayjs from 'dayjs'

/**
 * Safely converts various date formats to JavaScript Date object
 * @param {*} v - Value to convert (Firestore Timestamp, Date, or string)
 * @returns {Date|null} JavaScript Date or null if invalid
 */
export const toJsDateSafe = v => {
  if (!v) return null
  if (typeof v?.toDate === 'function') return v.toDate()
  if (v instanceof Date) return v
  if (v && typeof v.seconds === 'number') {
    const ms = v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6)
    return new Date(ms)
  }
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Checks if reception uses new data model (has type field)
 * @param {Object} doc - Reception document
 * @returns {boolean} True if new model
 */
export const isNewModel = doc => typeof doc?.type === 'string'

/**
 * Extracts type and purposes for display labels
 * @param {Object} doc - Reception document
 * @returns {{ type: string|null, purposes: string[] }}
 */
export function getLabelsInputs(doc) {
  if (!doc) return { type: null, purposes: [] }

  if (isNewModel(doc)) {
    const purposes = []

    // For expert type, include the specific service type
    if (doc.type === 'expert' && doc.expertService?.serviceType) {
      purposes.push(doc.expertService.serviceType)
    }

    return { type: doc.type, purposes }
  }

  // Legacy model - derive type from boolean flags
  let type = null
  if (doc.mightBuyProducts) {
    type = 'purchase'
  } else if (doc.hasProductsSoldOnBehalf) {
    type = 'auction'
  }

  const purposes = Array.isArray(doc.purposeOfSale) ? doc.purposeOfSale : []

  // Check if sales_order is in purposes (legacy way of marking sales orders)
  if (purposes.includes('sales_order')) {
    type = 'sales_order'
  }

  return { type, purposes }
}

/**
 * Gets personnel assigned to reception
 * @param {Object} doc - Reception document
 * @returns {string} Personnel email or empty string
 */
export function getPersonnelAssigned(doc) {
  if (!doc) return ''
  return doc.personnelAssigned ?? ''
}

/**
 * Calculates days remaining until deadline
 * @param {Object} doc - Reception document
 * @param {dayjs.Dayjs} now - Current date/time (for testing)
 * @returns {number} Days remaining (0 if no deadline)
 */
/**
 * Maps reception documents to ag-grid row data
 * @param {Array} receptions - Reception documents
 * @param {Function} t - Translation function
 * @param {string} dateFormat - Date format string
 * @param {Function} formatLabels - Label formatter function
 * @returns {Array} Mapped row data
 */
export function mapReceptionRows(receptions, t, dateFormat, formatLabels) {
  return (receptions || []).map(reception => {
    const { type, purposes } = getLabelsInputs(reception)
    const createdAtDate = toJsDateSafe(reception?.createdAt)
    const createdAtFormatted = createdAtDate ? dayjs(createdAtDate).format(dateFormat) : ''

    return {
      ...reception,
      createdAt: createdAtFormatted,
      daysLeft: getDaysLeft(reception),
      personnelAssigned: getPersonnelAssigned(reception),
      purposeOfSale: formatLabels(type, purposes, t),
    }
  })
}

export function getDaysLeft(doc, now = dayjs()) {
  if (!doc) return 0

  // All models: createdAt + deadline days
  const createdAtDate = toJsDateSafe(doc.createdAt)
  if (!createdAtDate) return 0

  const deadlineDays = Number(doc.deadline || 0)
  return dayjs(createdAtDate).add(deadlineDays, 'day').diff(now, 'day')
}
